/**
 * «Es fijo» en lo que Gavi ha encontrado: marca la categoría como pago fijo
 * y, si aún no tiene objetivo, le pone uno mensual con lo que se cobra (sin
 * objetivo, un pago fijo no sale en «Mensuales» ni en «Anuales»).
 */
import { send } from '@actual-app/core/platform/client/connection';
import * as monthUtils from '@actual-app/core/shared/months';
import { q } from '@actual-app/core/shared/query';
import type { NoteEntity } from '@actual-app/core/types/models';

import { plantillasDe } from '#components/mobile/anual/useAnual';
import {
  lineasDeObjetivo,
  notaConObjetivo,
} from '#components/mobile/budget/objetivos';
import type { Objetivo } from '#components/mobile/budget/objetivos';
import { usePagadas } from '#components/mobile/budget/usePagadas';
import { useCategories } from '#hooks/useCategories';
import { aqlQuery } from '#queries/aqlQuery';

import type { SerieCobro } from './cobros';

/**
 * Objetivo mensual que cubre las series de una categoría (Netflix y Spotify
 * en «Suscripciones» suman); lo que se cobra cada 2-3 meses se reparte. El
 * día de cargo, el de la serie principal si es mensual.
 */
export function objetivoDeSeries(
  series: readonly SerieCobro[],
): Objetivo | null {
  const importe = series.reduce(
    (total, s) => total + Math.ceil(s.importe / Math.max(1, s.cada)),
    0,
  );
  if (importe <= 0) {
    return null;
  }
  const principal = series[0];
  return {
    tipo: 'mensual',
    importe,
    dia: principal.cada === 1 ? principal.dia : null,
    modo: 'apartar',
  };
}

export function useMarcarFijoGavi(
  mes: string,
  porCategoria: ReadonlyMap<string, SerieCobro[]>,
) {
  const { setFijo } = usePagadas(mes);
  const { data: { list: categorias } = { list: [] } } = useCategories();

  return async (serie: SerieCobro) => {
    const id = serie.categoria;
    if (!id) {
      return;
    }
    const categoria = categorias.find(c => c.id === id);
    const objetivo = objetivoDeSeries(porCategoria.get(id) ?? [serie]);
    if (
      categoria &&
      objetivo &&
      plantillasDe(categoria.goal_def).length === 0
    ) {
      const { data } = await aqlQuery(q('notes').filter({ id }).select('*'));
      const nota = (data as NoteEntity[] | undefined)?.[0]?.note ?? null;
      await send('notes-save', {
        id,
        note: notaConObjetivo(
          nota,
          lineasDeObjetivo(objetivo, monthUtils.currentDay()),
        ),
      });
      if (categoria.template_settings?.source === 'ui') {
        await send('budget/set-category-automations', {
          categoriesWithTemplates: [{ id, templates: [] }],
          source: 'notes',
        });
      }
      await send('budget/store-note-templates', [id]);
      await send('budget/refresh-goals', { month: mes });
    }
    setFijo(id, true);
  };
}
